/* START_MODULE_CONTRACT
 * PURPOSE: Frontend UI module: CreateUserDialog.
 * SCOPE: React component or page.
 * DEPENDS: M-FE-API, M-FE-AUTH
 * LINKS: M-FE-ADMIN
 * END_MODULE_CONTRACT
 *
 * START_MODULE_MAP
 * CreateUserDialog - Frontend UI module: CreateUserDialog.
 * END_MODULE_MAP
 *
 * START_CHANGE_SUMMARY
 * LAST_CHANGE: 2026-07-02 - GRACE coverage: контракты функций, парные END_METHOD, блоки/логи для логики.
 * END_CHANGE_SUMMARY
 */

import { useRef, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import Collapse from '@mui/material/Collapse';
import { useDialogFormReady } from '../../../hooks/useDialogFormReady';
import { useBlurValidatedField } from '../../../hooks/useBlurValidatedField';
import { isMinLength, isNonEmptyTrimmed } from '../../../utils/formValidators';

/* START_METHOD_CreateUserDialog */
/* START_CONTRACT: CreateUserDialog
 * PURPOSE: Диалог создания пользователя (email + пароль) с валидацией и отправкой.
 * INPUTS: { open: boolean, onClose: ()=>void, onSubmit: (payload)=>Promise }.
 * OUTPUTS: { JSX.Element - диалог }.
 * SIDE_EFFECTS: вызов onSubmit (HTTP наверх); локальный state.
 * LINKS: M-FE-ADMIN
 * END_CONTRACT: CreateUserDialog
 */
export default function CreateUserDialog({ open, onClose, onSubmit }) {
  const firstFieldRef = useRef(null);
  const { formReady, resetFormReady, dialogTransitionSlotProps } = useDialogFormReady({ firstFieldRef });
  const emailField = useBlurValidatedField('', isNonEmptyTrimmed, formReady);
  const passwordField = useBlurValidatedField('', isMinLength(6), formReady);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const canSave = emailField.isValid && passwordField.isValid;

  /* START_METHOD_handleSubmit */
  /* START_CONTRACT: handleSubmit
   * PURPOSE: Проверить форму и создать пользователя; при успехе закрыть, иначе показать ошибку.
   * INPUTS: {}.
   * OUTPUTS: { Promise<void> }.
   * SIDE_EFFECTS: onSubmit (HTTP); setLoading/setError; handleClose.
   * LINKS: M-FE-ADMIN
   * END_CONTRACT: handleSubmit
   */
  const handleSubmit = async (event) => {
    if (event) event.preventDefault();
    if (!canSave || loading) {
      /* START_BLOCK_SUBMIT_BLOCKED */
      emailField.touch();
      passwordField.touch();
      return;
      /* END_BLOCK_SUBMIT_BLOCKED */
    }
    setLoading(true);
    setError('');
    try {
      await onSubmit({ email: emailField.value.trim(), password: passwordField.value });
      /* START_BLOCK_SUBMIT_OK */
      handleClose();
      /* END_BLOCK_SUBMIT_OK */
    } catch (err) {
      /* START_BLOCK_SUBMIT_FAIL */
      console.warn('[CreateUserDialog][handleSubmit][SUBMIT_FAIL] create user failed', {
        status: err.response?.status,
      });
      setError(err.response?.data?.message || 'Ошибка создания пользователя');
      /* END_BLOCK_SUBMIT_FAIL */
    } finally {
      setLoading(false);
    }
  };
  /* END_METHOD_handleSubmit */

  /* START_METHOD_handleClose */
  /* START_CONTRACT: handleClose
   * PURPOSE: Сбросить поля формы и закрыть диалог.
   * INPUTS: {}.
   * OUTPUTS: { void }.
   * SIDE_EFFECTS: setState reset; onClose.
   * LINKS: M-FE-ADMIN
   * END_CONTRACT: handleClose
   */
  const handleClose = () => {
    if (loading) return;
    emailField.reset('');
    passwordField.reset('');
    setError('');
    resetFormReady();
    onClose();
  };
  /* END_METHOD_handleClose */

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      slotProps={{ transition: dialogTransitionSlotProps }}
    >
      <form onSubmit={handleSubmit} noValidate>
        <DialogTitle sx={{ fontSize: '1rem' }}>Новый пользователь</DialogTitle>
        <DialogContent>
          <Collapse in={Boolean(error)} unmountOnExit>
            <Alert severity="error" sx={{ mb: 1 }} onClose={() => setError('')}>
              {error}
            </Alert>
          </Collapse>
          <TextField
            inputRef={firstFieldRef}
            required
            fullWidth
            label="Email"
            type="email"
            value={emailField.value}
            onChange={emailField.onChange}
            onBlur={emailField.onBlur}
            margin="normal"
            size="small"
            disabled={loading}
            error={emailField.showError}
            helperText={emailField.showError ? 'Введите email' : ' '}
          />
          <TextField
            required
            fullWidth
            label="Пароль"
            type="password"
            value={passwordField.value}
            onChange={passwordField.onChange}
            onBlur={passwordField.onBlur}
            margin="normal"
            size="small"
            disabled={loading}
            error={passwordField.showError}
            helperText={passwordField.showError ? 'Минимум 6 символов' : 'Минимум 6 символов'}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button size="small" onClick={handleClose} disabled={loading}>
            Отмена
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="success"
            size="small"
            loading={loading}
            disabled={!canSave}
          >
            Создать
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
/* END_METHOD_CreateUserDialog */
